import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import AppModal from "../../common/modal/AppModal";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isBox, isContainedView } from "../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox } from "../../../../common/common_tools";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../../utils/sceneAddSpinner";
import { addScenarioElementToCurrentScene, addSound } from "../../../../common/actions";
import { defaultForElement } from "../../../../plugins/InteractivePlugin/schema";
import { fetchIdealStudioFiles, deleteIdealStudioFile } from "../../../../common/server_utils.es6";
import { getToken } from "../../../../common/environment_config.es6";
import { relativeTime } from "../../../utils/relativeTime";
import { isClmAllowedPlugin } from "../../../../common/clm";
import AssetGrid from "../elements_library/ui/AssetGrid";
import AssetCell from "../elements_library/ui/AssetCell";
import AssetPlaceholder from "../elements_library/ui/AssetPlaceholder";
import LazyAssetImage from "../elements_library/ui/LazyAssetImage";
import VideoThumb from "../../timeline/IdeaTimeline/components/VideoThumb";
import FileDetailsDrawer from "./FileDetailsDrawer";
import { motionSourceFromFileRow } from "../../add_image_modal/sceneVisualService";
import PanelSearch from "../elements_library/ui/PanelSearch";
import EmptyState from "../elements_library/ui/EmptyState";
import {
  LuSearch,
  LuX,
  LuImage,
  LuMusic2,
  LuClapperboard,
  LuFilm,
  LuFileText,
  LuFileSpreadsheet,
  LuUserRound,
  LuMicVocal,
  LuMonitorPlay,
  LuWandSparkles,
  LuDownload,
  LuTrash2,
  LuPlay,
  LuPause,
  LuMousePointerClick,
  LuLoaderCircle,
  LuInfo,
} from "react-icons/lu";
import { openInDirector } from "../audio/video-director/openInDirector";
import { downloadFile } from "./downloadFile";

const TYPE_PLUGIN_CANDIDATES = {
  image: { media: "image", names: ["HotspotImages"] },
  animation: { media: "image", names: ["HotspotImages"] },
  video: { media: "video", names: ["EnrichedPlayer"] },
  audio: { media: "audio", names: ["AudioCue", "EnrichedAudio"] },
  pdf: { media: "pdf", names: ["EnrichedPDF"] },
  html: { media: "html", names: ["HtmlContent"] },
};

// CLM courses: only allowlisted plugins may be added (spec §3.3 item 1) — an
// isClm=true caller only gets a match among def.names that's also allowlisted,
// so video/audio/pdf resolve to null (no plugin, no drag/add handlers) while
// image (HotspotImages) and html (HtmlContent) still work.
const resolvePlugin = (fileType, isClm) => {
  const def = TYPE_PLUGIN_CANDIDATES[(fileType || "").toLowerCase()];
  if (!def) return null;
  const name = def.names.find(
    (n) => IdealStudio.Plugins.get(n) && (!isClm || isClmAllowedPlugin(n))
  );
  return name ? { name, media: def.media } : null;
};

// fileType filtresi → API'ye gönderilecek değer listesi
const FILE_TYPE_MAP = {
  // "Resim" = statik görseller. DB fileType'ı kaba kategori ('image') tuttuğu
  // için gif/webp de 'image'dir; statik/hareketli ayrımı FILE_EXTENSION_MAP ile
  // uzantıdan yapılır (aşağı bak).
  image: ["image"],
  audio: ["audio", "audio/mpeg", "audio/wav", "audio/ogg", "audio/mp3", "clone-voice"],
  video: [
    "video",
    "video/mp4",
    "video/webm",
    "video/mov",
    "course-video",
    "tavus-video",
    "lipsync-video",
    "azure-avatar",
    "video/quicktime",
    // "animation" (animate-v2 webp/gif) bilerek yok — "Hareketli Resim" filtresinde.
  ],
  avatar: ["tavus-video", "lipsync-video", "azure-avatar"],
  clonevoice: ["clone-voice"],
  coursevideo: ["course-video"],
  // "Hareketli Resim": animate-v2 çıktıları (fileType='animation', daima
  // webp/gif) + fileType='image' olup uzantısı gif/webp olan yüklenen/AI
  // görseller. Uzantı whitelist'i (FILE_EXTENSION_MAP) ile ayrılır.
  animatedimage: ["animation", "image"],
};

// image / animatedimage filtresi fileType'a EK olarak uzantı whitelist'i de
// gönderir (backend fileType[] AND fileExtension[] birlikte uygular). Böylece
// "Resim" statik uzantılarla sınırlanır, "Hareketli Resim" yalnız gif/webp'i
// yakalar. Uzantılar DB'de noktasız saklanır (upload/animate-v2/image-editing
// hepsi `.replace('.','')`).
const FILE_EXTENSION_MAP = {
  image: ["png", "jpg", "jpeg", "svg", "bmp"],
  animatedimage: ["gif", "webp", "apng", "avif"],
};

const IMAGE_LIKE_EXT = ["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"];
const isImageLikeUrl = (url) => {
  const clean = (url || "").split("?")[0];
  const dot = clean.lastIndexOf(".");
  return dot !== -1 && IMAGE_LIKE_EXT.includes(clean.substring(dot + 1).toLowerCase());
};

// Director aspect from the row's pixel size when the library knows it (else the server default, 16:9).
export const imageAspect = (raw) => {
  const w = Number(raw?.metadata?.width ?? raw?.width);
  const h = Number(raw?.metadata?.height ?? raw?.height);
  return w > 0 && h > 0 ? (w > h ? "16:9" : "9:16") : undefined;
};

// Plain uploads are written with provider "external" and no model/prompt/metadata.
const isGeneratedRow = (raw) =>
  !!(
    raw?.metadata?.videoProjectId ||
    raw?.model ||
    raw?.prompt ||
    (raw?.provider && raw.provider !== "external")
  );

// Frame of a video row; falls back to the type icon when no frame can be decoded.
const VideoCellThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <AssetPlaceholder type="video" />
  ) : (
    <div className="cmp-lazyimg">
      <VideoThumb url={url} className="cmp-lazyimg__img is-loaded" onFail={() => setFailed(true)} />
    </div>
  );
};
VideoCellThumb.propTypes = { url: PropTypes.string };

const formatDuration = (sec) => {
  const s = Math.round(sec || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Same chip look as BetaTag (common/BetaTag.jsx), brand-tinted, top-left.
const aiChipStyle = {
  position: "absolute",
  top: 4,
  left: 4,
  padding: "0 4px",
  borderRadius: 3,
  fontSize: 8,
  fontWeight: 700,
  lineHeight: "12px",
  background: "#f3e8ff",
  color: "#6b21a8",
  border: "1px solid #d8b4fe",
  zIndex: 2,
};

// Küçük yuvarlak-kare aksiyon butonu (ses satırı + grid ses kartı ekleme butonları).
const miniBtnStyle = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#6b7280",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

// Director project status → draft phase badge (DraftStatus key).
const DRAFT_PHASE = {
  prefilling: "brief",
  brief: "brief",
  treating: "treatment",
  treatment: "treatment",
  storyboard: "storyboard",
  editing: "editing",
};
const dt = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);

const MyFilesPanel = (props) => {
  const {
    isClm,
    filesUploaded,
    onNavItemsAdded,
    onIndexSelected,
    onNavItemSelected,
    navItemsIds,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    boxes,
    onBoxAdded,
    onElementSelected,
    boxSelected,
    pluginToolbars,
    fileModalResult,
    onClose,
  } = props;

  const dispatch = useDispatch();

  const mockFiles = [
    {
      id: "mock-1",
      name: "ai-images.png",
      thumbnail:
        "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800",
      fileType: "image",
    },
    {
      id: "mock-2",
      name: "young-man-run.png",
      thumbnail:
        "https://images.pexels.com/photos/5069142/pexels-photo-5069142.jpeg?auto=compress&cs=tinysrgb&w=800",
      fileType: "image",
    },
    {
      id: "mock-3",
      name: "sunny-days.png",
      thumbnail:
        "https://images.pexels.com/photos/1998439/pexels-photo-1998439.jpeg?auto=compress&cs=tinysrgb&w=800",
      fileType: "image",
    },
    {
      id: "mock-4",
      name: "mountainPeakView.png",
      thumbnail:
        "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg?auto=compress&cs=tinysrgb&w=800",
      fileType: "image",
    },
    {
      id: "mock-5",
      name: "cityLightsAtNight.png",
      thumbnail:
        "https://images.pexels.com/photos/316093/pexels-photo-316093.jpeg?auto=compress&cs=tinysrgb&w=800",
      fileType: "image",
    },
  ];

  const FILES_PAGE_SIZE = 100;

  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({ offset: 0, hasNext: false, total: 0 });
  const [error, setError] = useState(null);
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  // Ekrandaki `files` hangi fileType|scene filtresine ait? Chip değişince state
  // önce güncellenir, files bir frame sonra → uyuşmuyorsa (stale) eski veriyi
  // yeni filtreyle çizmeyiz (yanlış isim flicker'ını önler).
  const [loadedFilterKey, setLoadedFilterKey] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sceneFilter, setSceneFilter] = useState("all"); // "all" | navItemId
  const [isSceneOpen, setIsSceneOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [durations, setDurations] = useState({}); // cardId -> saniye (metadata/play ile dolar)
  // Metadata yüklenemeyen (bozuk/CORS/Infinity) satırlar — spinner sonsuz dönmesin,
  // "0:00" fallback'ine düşer.
  const [durationFailed, setDurationFailed] = useState({});
  const audioRef = useRef(null);
  // Canlı Audio elemanının ait olduğu kart id'si — pause sonrası aynı satırı
  // kaldığı yerden sürdürmek/seek etmek için tutulur.
  const activeAudioIdRef = useRef(null);
  // Server'dan en son çekilen sorgunun imzası (search|type). Sahne↔Tüm geçişinde
  // sorgu değişmediyse yeniden çekmeyiz → gereksiz istek + flicker önlenir.
  const lastFetchedKey = useRef(null);
  // Filtre başına sonuç cache'i: key -> { files, pagination }. Bir filtreye geri
  // dönünce anında (senkron) gösterilir, yeniden fetch edilmez → flicker biter.
  const filesCacheRef = useRef({});
  // Son fileType|scene imzası — chip/sahne değişimini arama yazımından ayırıp
  // tıklama anında yükleme göstermek için.
  const prevFilterRef = useRef(null);
  const [detailsFile, setDetailsFile] = useState(null);
  // aidea:libraryChanged (a Director render/import landed) bumps this → refetch.
  const [refreshTick, setRefreshTick] = useState(0);
  // "Video drafts" chip: Director projects of this training, not library files.
  const [drafts, setDrafts] = useState(null); // null = loading · { items, error }
  const isDrafts = fileTypeFilter === "videodrafts";

  useEffect(() => {
    if (!isDrafts) return undefined;
    let cancelled = false;
    if (!window.idealStudioTrainingId) {
      setDrafts({ items: [] });
      return undefined;
    }
    setDrafts(null);
    const base = process.env.MW_API_URL || window.ideaLibraryRootUrl || "";
    const tid = encodeURIComponent(window.idealStudioTrainingId);
    fetch(`${base}api/video-director/projects/list?trainingId=${tid}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.status))))
      .then((items) => !cancelled && setDrafts({ items: items || [] }))
      .catch(() => !cancelled && setDrafts({ items: [], error: true }));
    return () => {
      cancelled = true;
    };
  }, [isDrafts, refreshTick]);

  useEffect(() => {
    const onChanged = () => {
      filesCacheRef.current = {};
      lastFetchedKey.current = null;
      setRefreshTick((n) => n + 1);
    };
    window.addEventListener("aidea:libraryChanged", onChanged);
    return () => window.removeEventListener("aidea:libraryChanged", onChanged);
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // id.url'i taze bir Audio'ya yükleyip atTime'dan çalar. Süre metadata gelince
  // durations'a yazılır (satırdaki 0:00 / X:XX için).
  const startAudio = (id, url, atTime = 0) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    if (atTime) audio.currentTime = atTime;
    audio.onloadedmetadata = () =>
      setDurations((d) => (d[id] ? d : { ...d, [id]: audio.duration }));
    audio.onended = () => {
      setPlayingId(null);
      setCurrentTime(0);
    };
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
    audioRef.current = audio;
    activeAudioIdRef.current = id;
    audio.play().catch(() => {});
    setPlayingId(id);
    setCurrentTime(atTime);
  };

  const togglePlayAudio = (id, url) => {
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (activeAudioIdRef.current === id && audioRef.current) {
      audioRef.current.play().catch(() => {});
      setPlayingId(id);
      return;
    }
    startAudio(id, url);
  };

  const seekAudio = (id, url, dur, e) => {
    if (!dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const time = ratio * dur;
    if (activeAudioIdRef.current === id && audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      if (playingId !== id) {
        audioRef.current.play().catch(() => {});
        setPlayingId(id);
      }
      return;
    }
    startAudio(id, url, time);
  };

  const basicFilters = [
    { key: "all", label: i18n.t("all") || "All", Icon: LuSearch },
    { key: "image", label: i18n.t("image") || "Image", Icon: LuImage },
    { key: "audio", label: i18n.t("audio") || "Audio", Icon: LuMusic2 },
    { key: "video", label: i18n.t("video") || "Video", Icon: LuClapperboard },
    { key: "pdf", label: i18n.t("pdf") || "PDF", Icon: LuFileText },
    { key: "csv", label: i18n.t("csv") || "CSV", Icon: LuFileSpreadsheet },
    { key: "videodrafts", label: i18n.t("library_video_drafts"), Icon: LuFilm },
  ];

  const advancedFilters = [
    { key: "avatar", label: i18n.t("Avatar") || "Avatar", Icon: LuUserRound },
    { key: "clonevoice", label: i18n.t("ClonedVoices") || "Klon Ses", Icon: LuMicVocal },
    {
      key: "coursevideo",
      label: i18n.t("Storyboard") || "Storyboard Videoları",
      Icon: LuMonitorPlay,
    },
    {
      key: "animatedimage",
      label: i18n.t("AnimatedImage") || "Hareketli Resim",
      Icon: LuWandSparkles,
    },
  ];

  // Ses dosyasının süresini almak için yardımcı fonksiyon
  const getAudioDuration = (url) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => {
        const durationInSeconds = audio.duration;
        resolve(durationInSeconds);
      });
      audio.addEventListener("error", () => {
        resolve(30); // Varsayılan 30 saniye
      });
      audio.src = url;
    });
  };

  // Video dosyasının süresini almak için yardımcı fonksiyon
  const getVideoDuration = (url) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.addEventListener("loadedmetadata", () => {
        const durationInSeconds = video.duration;
        resolve(durationInSeconds);
      });
      video.addEventListener("error", () => {
        resolve(60); // Varsayılan 60 saniye
      });
      video.src = url;
    });
  };

  // extra.motionSource: Hareketli Görsel GIF/WebP'sinin kaynak dokümanı —
  // createBox kutu state'ine yazar, pill'deki "AI ile düzenle" onu okur.
  const addFileAsBox = async (fileType, url, extra = {}) => {
    if (!onBoxAdded || !url) {
      return;
    }

    // Bazı özel servisler (ör. Tavus) dosya tipini "file" döndürebiliyor.
    // Böyle bir durumda URL uzantısına göre tekrar sınıflandır.
    if (fileType === "file" && typeof url === "string") {
      const cleanUrl = url.split("?")[0];
      const dotIndex = cleanUrl.lastIndexOf(".");
      if (dotIndex !== -1) {
        const extFromUrl = cleanUrl.substring(dotIndex + 1).toLowerCase();
        if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(extFromUrl)) {
          fileType = "image";
        } else if (["mp3", "wav", "ogg"].includes(extFromUrl)) {
          fileType = "audio";
        } else if (["mp4", "mov", "webm", "mkv"].includes(extFromUrl)) {
          fileType = "video";
        } else if (extFromUrl === "csv") {
          fileType = "csv";
        } else if (extFromUrl === "pdf") {
          fileType = "pdf";
        }
      }
    }

    const t = (fileType || "").toLowerCase();
    // Interactive scene: route image/video directly as scenario elements
    const currentNavItem = navItems && navItemSelected ? navItems[navItemSelected] : null;
    const sceneIsInteractive = !!currentNavItem?.boxes?.some(
      (bId) => pluginToolbars?.[bId]?.pluginId === "InteractivePlugin"
    );
    const interactiveBoxId = sceneIsInteractive
      ? currentNavItem.boxes.find((bId) => pluginToolbars?.[bId]?.pluginId === "InteractivePlugin")
      : null;

    if (sceneIsInteractive && interactiveBoxId) {
      if (t === "image") {
        const el = { ...defaultForElement("visualimage"), imageUrl: url };
        dispatch(addScenarioElementToCurrentScene(interactiveBoxId, el));
        return;
      }
      if (t === "video") {
        const el = { ...defaultForElement("videocard"), videoUrl: url };
        dispatch(addScenarioElementToCurrentScene(interactiveBoxId, el));
        return;
      }
    }

    const resolved = resolvePlugin(t, isClm);
    const pluginName = resolved ? resolved.name : null;

    if (!pluginName) {
      return;
    }

    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) {
      return;
    }

    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];

    if (!navItem) {
      return;
    }

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);

    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    // Pozisyon + id await gerektirmez → hemen üret. Görsel/video/ses için
    // spinner'ı TIKLAMA ANINDA göster; kutu render olunca üstüne geçip medya
    // (video/img yüklenmesi ya da <audio> metadata'sı) hazır olunca kapanır.
    const position = {
      x: randomPositionGenerator(10, 30),
      y: randomPositionGenerator(10, 30),
      type: "absolute",
    };
    const boxId = ID_PREFIX_BOX + Date.now();
    if (t === "image" || t === "video" || t === "audio") {
      spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv, assetType: t });
    }

    // Audio still clones a selected box; image/video use the metadata helper.
    let width;
    let height;
    let widthUnit;
    let heightUnit;

    if (t === "image" || t === "video") {
      ({ width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, t));
    } else if (boxSelected && pluginToolbars && pluginToolbars[boxSelected]) {
      const selStruct = pluginToolbars[boxSelected].structure;
      if (selStruct) {
        const scale = 0.6; // shrink to 60% of original size
        const w = parseFloat(selStruct.width);
        const h = parseFloat(selStruct.height);
        width = isNaN(w) ? selStruct.width : w * scale;
        height = isNaN(h) ? selStruct.height : h * scale;
        widthUnit = selStruct.widthUnit;
        heightUnit = selStruct.heightUnit;
      }
    }

    // Ses/Video dosyası için süreyi al
    let mediaDurationSeconds = 30; // Varsayılan
    if (t === "audio") {
      mediaDurationSeconds = await getAudioDuration(url);
    } else if (t === "video") {
      mediaDurationSeconds = await getVideoDuration(url);
    }

    const FPS = 30;

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      // Source marker so "Replace File" reopens this same tab (My Files).
      componentType: "MyFiles",
      position,
      id: boxId,
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      // TemplatesModal pattern: pass media URL directly on ids so plugin picks it as toolbar.state.url
      url,
      // HtmlContent embeds the file as an iframe via its _html state (a URL → iframe).
      initialState: pluginName === "HtmlContent" ? { _html: url } : undefined,
      // If there is a selected box, clone its size units so aspect ratio & size match modal-added elements
      width,
      height,
      widthUnit,
      heightUnit,
      ...(extra.motionSource ? { motionSource: extra.motionSource } : {}),
      // Placement is owned by addBoxThunk (shared media rows: same-type
      // elements append back-to-back). Only the known duration is passed on.
      timeline:
        t === "audio" || t === "video"
          ? {
              durationHintFrames: Math.ceil(mediaDurationSeconds * FPS),
              visible: true,
            }
          : undefined,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  // Ses dosyasını sahnenin soundList'ine ekler (narration'ın kullandığı yol).
  const addAsSceneSound = async (item) => {
    if (!navItemSelected || !item.url) return;
    const dur = durations[item.id] || (await getAudioDuration(item.url));
    dispatch(
      addSound(navItemSelected, {
        file: item.url,
        name: item.name,
        startTime: 0,
        endTime: dur,
        volumeLevel: 1,
      })
    );
  };

  // Ses filtresi aktifken bir dosyayı Image 1 tarzı satır olarak çizer:
  // ad + play/pause + progress + süre + cue/sahne ekleme + indir/sil.
  const renderAudioRow = (file, index) => {
    const cardId = file.id || index;
    const name = file?.name || `file-${index + 1}`;
    const url = file?.raw?.fileVirtualPath || file?.thumbnail;
    const dur = durations[cardId] || 0;
    const isActive = activeAudioIdRef.current === cardId;
    const isPlaying = playingId === cardId;
    const shown = isActive ? currentTime : 0;
    const pct = Math.min(100, (shown / (dur || 1)) * 100);
    return (
      <div
        key={cardId}
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 12px",
        }}
      >
        <div
          title={name}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginBottom: 8,
          }}
        >
          {name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={() => togglePlayAudio(cardId, url)}
            title={i18n.t("listen") || i18n.t("preview")}
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {isPlaying ? (
              <LuPause size={13} strokeWidth={2.25} />
            ) : (
              <LuPlay size={13} strokeWidth={2.25} />
            )}
          </button>

          <div
            onClick={(e) => seekAudio(cardId, url, dur, e)}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: "#e5e7eb",
              position: "relative",
              cursor: dur ? "pointer" : "default",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: `${pct}%`,
                borderRadius: 3,
                background: "#4f46e5",
              }}
            />
          </div>

          <span
            style={{
              flexShrink: 0,
              fontSize: 11,
              color: "#6b7280",
              minWidth: 72,
              textAlign: "right",
            }}
          >
            {formatDuration(shown)} / {formatDuration(dur)}
          </span>

          <button
            type="button"
            onClick={() => addFileAsBox("audio", url)}
            title={i18n.t("sound_add_cue") || "Tıklama sesi olarak ekle"}
            style={miniBtnStyle}
          >
            <LuMousePointerClick size={14} strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={() => addAsSceneSound({ id: cardId, url, name })}
            title={i18n.t("sound_add_scene") || "Sahne sesi olarak ekle"}
            style={miniBtnStyle}
          >
            <LuClapperboard size={14} strokeWidth={2.25} />
          </button>

          <button
            type="button"
            onClick={() => downloadFile(url, name)}
            title={i18n.t("download") || "İndir"}
            style={miniBtnStyle}
          >
            <LuDownload size={14} strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={() => {
              if (!file.id && file.id !== 0) return;
              setConfirmDelete({ id: file.id, name });
            }}
            title={i18n.t("delete") || "Sil"}
            style={{ ...miniBtnStyle, color: "#ef4444" }}
          >
            <LuTrash2 size={14} strokeWidth={2.25} />
          </button>
        </div>
      </div>
    );
  };

  const mapFile = (f) => {
    let ext = (f.fileExtension || "").toLowerCase().replace(/^\./, "");
    const mime = (f.fileType || f.file_type || "").toLowerCase();

    // Eğer backend extension boş bıraktıysa, URL veya isimden tahmin et
    if (!ext) {
      const pathSource = (f.fileVirtualPath || f.fileName || f.filename || "").split("?")[0];
      const dotIndex = pathSource.lastIndexOf(".");
      if (dotIndex !== -1) {
        ext = pathSource.substring(dotIndex + 1).toLowerCase();
      }
    }

    let inferredType = "file";

    // 1) Önce mime tipine bak: image/jpeg, image/png vs -> hepsi "image"
    if (mime.startsWith("image/")) {
      inferredType = "image";
    } else if (mime === "animation") {
      // Animasyonlu webp <img>'de (HotspotImages) oynar — OrgFilesPanel.inferFileType
      // ile aynı davranış (bkz. o dosyadaki aynı satır). Önceden "video" dönüyordu,
      // bu da TYPE_PLUGIN_CANDIDATES.animation → HotspotImages eşlemesini hiç
      // kullanılmaz kılıp EnrichedPlayer'a (video kutusu, mp4Url ile) düşürüyordu.
      inferredType = "image";
    } else if (mime === "html") {
      inferredType = "html";
    } else if (mime.startsWith("audio/")) {
      inferredType = "audio";
    } else if (mime.startsWith("video/")) {
      inferredType = "video";
    } else if (mime === "text/csv") {
      inferredType = "csv";
    } else if (mime === "application/pdf") {
      inferredType = "pdf";
    } else {
      // 2) Mime yoksa/garipse uzantıya göre karar ver
      if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(ext)) {
        inferredType = "image";
      } else if (["mp3", "wav", "ogg"].includes(ext)) {
        inferredType = "audio";
      } else if (["mp4", "mov", "webm", "mkv"].includes(ext)) {
        inferredType = "video";
      } else if (ext === "csv") {
        inferredType = "csv";
      } else if (ext === "pdf") {
        inferredType = "pdf";
      } else if (ext === "html" || ext === "htm") {
        inferredType = "html";
      }
    }

    return {
      id: f.id,
      name: f.fileName,
      thumbnail: f.fileVirtualPath,
      fileType: inferredType,
      generated: isGeneratedRow(f),
      raw: f,
    };
  };

  const loadFiles = async (offset, isCancelledRef) => {
    const append = offset > 0;
    const fk = `${fileTypeFilter}|${sceneFilter}`;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const useExtension = fileTypeFilter === "pdf" || fileTypeFilter === "csv";
      // image/animatedimage: fileType'a ek uzantı filtresi (statik vs gif/webp).
      const extFilter = FILE_EXTENSION_MAP[fileTypeFilter];
      const response = await fetchIdealStudioFiles({
        search: searchTerm || undefined,
        limit: FILES_PAGE_SIZE,
        offset,
        fileType:
          fileTypeFilter === "all" || useExtension
            ? undefined
            : (FILE_TYPE_MAP[fileTypeFilter] ?? fileTypeFilter),
        fileExtension: useExtension ? [fileTypeFilter, `.${fileTypeFilter}`] : extFilter,
        // Sahne filtresi: seçili sahnenin navId'sini backend'e gönder → nav_ids
        // jsonb dizisinde bu navId'yi içeren tüm dosyalar (hâlâ referanslı olsun
        // olmasın) gelir. "all" iken sahne filtresi yok, kullanıcının tüm dosyaları.
        navId: sceneFilter !== "all" ? sceneFilter : undefined,
      });

      if (isCancelledRef.current) return;

      const cacheKey = `${searchTerm}|${fileTypeFilter}|${sceneFilter}`;

      if (response && Array.isArray(response.files)) {
        const mapped = response.files.map(mapFile);

        const nextPagination = {
          offset: response.offset ?? offset,
          hasNext: !!response.hasNext,
          total: response.total ?? mapped.length,
        };
        setPagination(nextPagination);

        let nextFiles;
        if (append) {
          nextFiles = null; // prev'e bağlı; updater içinde cache'lenir
          setFiles((prev) => {
            const merged = [...prev, ...mapped];
            filesCacheRef.current[cacheKey] = { files: merged, pagination: nextPagination };
            return merged;
          });
        } else if (mapped.length > 0) {
          nextFiles = mapped;
          setFiles(mapped);
        } else {
          // Sadece genel görünümde (all) mockFiles göster; spesifik filtre boş ise grid de boş olsun
          nextFiles = fileTypeFilter === "all" && sceneFilter === "all" ? mockFiles : [];
          setFiles(nextFiles);
        }
        if (nextFiles !== null) {
          filesCacheRef.current[cacheKey] = { files: nextFiles, pagination: nextPagination };
        }
      } else if (!append) {
        setPagination({ offset: 0, hasNext: false, total: 0 });
        setFiles(fileTypeFilter === "all" && sceneFilter === "all" ? mockFiles : []);
      }
    } catch (e) {
      if (!isCancelledRef.current) {
        console.error("Failed to load files", e);
        if (append) {
          // "Daha fazla yükle" başarısız olsa da mevcut listeyi bozma
        } else {
          setError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
          setPagination({ offset: 0, hasNext: false, total: 0 });
          setFiles(fileTypeFilter === "all" && sceneFilter === "all" ? mockFiles : []);
        }
      }
    } finally {
      if (!isCancelledRef.current) {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
          setLoadedFilterKey(fk); // artık ekrandaki files bu filtreye ait
        }
      }
    }
  };

  const handleLoadMore = () => {
    if (loading || loadingMore || !pagination.hasNext) return;
    const isCancelledRef = { current: false };
    loadFiles(pagination.offset + FILES_PAGE_SIZE, isCancelledRef);
  };

  useEffect(() => {
    if (isDrafts) {
      setLoading(false);
      return undefined;
    }
    // Aynı sorgu için dosyalar zaten yüklü → tekrar çekme, mevcut listeyi göster.
    const key = `${searchTerm}|${fileTypeFilter}|${sceneFilter}`;
    const filterKey = `${fileTypeFilter}|${sceneFilter}`;
    // Chip/sahne değişimi mi? (arama yazımı değil) → anında yükleme göster.
    const filterChanged = prevFilterRef.current !== null && prevFilterRef.current !== filterKey;
    prevFilterRef.current = filterKey;

    if (lastFetchedKey.current === key) {
      setLoading(false);
      return;
    }

    // Bu filtre daha önce çekildiyse cache'ten SENKRON göster → başka filtrenin
    // verisiyle 250ms flicker etme, yeniden fetch de yok.
    const cached = filesCacheRef.current[key];
    if (cached) {
      lastFetchedKey.current = key;
      setFiles(cached.files);
      setPagination(cached.pagination);
      setLoadedFilterKey(filterKey);
      setLoading(false);
      return;
    }

    // Filtreye tıklanır tıklanmaz eski veriyi gizle + "Yükleniyor" göster; böylece
    // önce eski filtrenin listesini gösterip sonra güncelleme flicker'ı olmaz.
    if (filterChanged) {
      setFiles([]);
      setLoading(true);
    }

    const isCancelledRef = { current: false };

    const timeoutId = setTimeout(() => {
      lastFetchedKey.current = key;
      loadFiles(0, isCancelledRef);
    }, 250);

    return () => {
      isCancelledRef.current = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, fileTypeFilter, sceneFilter, refreshTick]);

  useEffect(() => {
    if (fileModalResult && fileModalResult.accept) {
      const acc = fileModalResult.accept.toLowerCase();
      if (acc.includes("pdf")) setFileTypeFilter("pdf");
      else if (acc.includes("image")) setFileTypeFilter("image");
      else if (acc.includes("video")) setFileTypeFilter("video");
      else if (acc.includes("audio")) setFileTypeFilter("audio");
    }
  }, [fileModalResult]);

  // Sahne seçenekleri: navItemsIds sırası = sahne sırası (1. sıra = 1. sahne).
  const sceneOptions = useMemo(
    () =>
      (navItemsIds || []).map((id, i) => ({
        id,
        label: `${i18n.t("scene") || "Sahne"} ${i + 1}`,
      })),
    [navItemsIds]
  );

  // Grid'de gösterilecek liste: sahne modu dahil hepsi server'dan `files`.
  // Sahne modunda arama/tip filtresi de backend'e navId ile birlikte gider.
  const displayFiles = files;
  const visibleDrafts = (drafts?.items || []).filter((d) =>
    (d.title || dt("Untitled")).toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  // `files` mevcut filtreye ait değilse (chip yeni set edildi, fetch/cache henüz
  // güncellemedi) eski veriyi çizme → yanlış isim flicker'ı olmaz.
  const stale = loadedFilterKey !== `${fileTypeFilter}|${sceneFilter}`;

  // Ses satırının kaynak URL'i (renderAudioRow ile aynı) — preload/hazır kontrolü için.
  const audioRowUrl = (file) => file?.raw?.fileVirtualPath || file?.thumbnail;
  const isAudioFilter = fileTypeFilter === "audio" || fileTypeFilter === "clonevoice";
  // Tüm ses satırlarının süresi (metadata) yüklendi mi? Bitene kadar ses bölümü
  // "Yükleniyor..." gösterir; hepsi hazır olunca sesler birden çizilir.
  const audioMetaReady =
    !isAudioFilter ||
    displayFiles.every((file, index) => {
      const cardId = file.id || index;
      const url = audioRowUrl(file);
      return !url || durations[cardId] || durationFailed[cardId];
    });

  const activeSceneLabel =
    sceneFilter === "all"
      ? i18n.t("scene") || "Sahne"
      : `${i18n.t("scene") || "Sahne"} ${(navItemsIds || []).indexOf(sceneFilter) + 1}`;

  return (
    <div
      className="library-myfiles-panel"
      style={{
        flex: 1,
        maxHeight: "calc(100vh - 105px)",
        minHeight: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        // The details drawer fills the panel, not just the (short) grid.
        ...(detailsFile ? { height: "calc(100vh - 105px)" } : {}),
      }}
    >
      {detailsFile && (
        <FileDetailsDrawer
          file={detailsFile.file}
          fileType={detailsFile.type}
          durationText={detailsFile.durationText}
          onClose={() => setDetailsFile(null)}
          onOpenDirector={(projectId) =>
            window.dispatchEvent(
              new CustomEvent("aidea:openDirectorProject", { detail: { projectId } })
            )
          }
        />
      )}
      <div
        className="library-myfiles-header"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 16,
          padding: "0 10px",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PanelSearch
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm("")}
            placeholder={i18n.t("search") || "Ara"}
          />
          <div className="library-myfiles-scene-wrapper" style={{ position: "relative" }}>
            <button
              type="button"
              className="library-myfiles-scene-button"
              onClick={() => {
                setIsFilterOpen(false);
                setIsSceneOpen((open) => !open);
              }}
              title={
                sceneFilter === "all"
                  ? i18n.t("filter_by_scene") || "Sahneye göre filtrele"
                  : activeSceneLabel
              }
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid",
                borderColor: sceneFilter !== "all" ? "#7c3aed" : "#e5e7eb",
                backgroundColor: sceneFilter !== "all" ? "#f3e8ff" : "#ffffff",
                color: sceneFilter !== "all" ? "#6b21a8" : "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <LuClapperboard size={18} />
            </button>

            {isSceneOpen && (
              <div
                className="library-myfiles-scene-dropdown"
                style={{
                  position: "absolute",
                  top: 44,
                  right: 0,
                  minWidth: 140,
                  maxHeight: 260,
                  overflowY: "auto",
                  padding: 6,
                  borderRadius: 12,
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "0 10px 15px -3px rgba(15,23,42,0.15), 0 4px 6px -4px rgba(15,23,42,0.1)",
                  border: "1px solid #e5e7eb",
                  zIndex: 20,
                }}
              >
                {[{ id: "all", label: i18n.t("all_files") || "Tüm Dosyalar" }, ...sceneOptions].map(
                  (opt) => {
                    const isActive = sceneFilter === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          setSceneFilter(opt.id);
                          setIsSceneOpen(false);
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          border: "none",
                          outline: "none",
                          backgroundColor: isActive ? "#eff6ff" : "transparent",
                          color: isActive ? "#1d4ed8" : "#374151",
                          borderRadius: 8,
                          padding: "6px 8px",
                          fontSize: 13,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  }
                )}
              </div>
            )}
          </div>
          <div className="library-myfiles-filter-wrapper" style={{ position: "relative" }}>
            <button
              type="button"
              className="library-myfiles-filter-button"
              onClick={() => {
                setIsSceneOpen(false);
                setIsFilterOpen((open) => !open);
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="3 4 21 4 14 12.94 14 19 10 21 10 12.94 3 4" />
              </svg>
            </button>

            {isFilterOpen && (
              <div
                className="library-myfiles-filter-dropdown"
                style={{
                  position: "absolute",
                  top: 44,
                  right: 0,
                  minWidth: 140,
                  padding: 6,
                  borderRadius: 12,
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "0 10px 15px -3px rgba(15,23,42,0.15), 0 4px 6px -4px rgba(15,23,42,0.1)",
                  border: "1px solid #e5e7eb",
                  zIndex: 20,
                }}
              >
                {[...basicFilters, ...advancedFilters].map((opt) => {
                  const isActive = fileTypeFilter === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setFileTypeFilter(opt.key);
                        setIsFilterOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        outline: "none",
                        backgroundColor: isActive ? "#eff6ff" : "transparent",
                        color: isActive ? "#1d4ed8" : "#374151",
                        borderRadius: 8,
                        padding: "6px 8px",
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              className="cmp-panel__close"
              onClick={onClose}
              title={i18n.t("close")}
            >
              <LuX size={15} />
            </button>
          )}
        </div>
        <div
          className="library-myfiles-quickfilters"
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginTop: 2,
            alignItems: "center",
          }}
        >
          {basicFilters.map((opt) => {
            const active = fileTypeFilter === opt.key;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFileTypeFilter(opt.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 8px",
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: active ? "#7c3aed" : "#e5e7eb",
                  backgroundColor: active ? "#f3e8ff" : "#ffffff",
                  color: active ? "#6b21a8" : "#374151",
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <Icon size={14} />
                <span>{opt.label}</span>
              </button>
            );
          })}

          {/* + butonu: sadece kapalıyken göster */}
          {!isAdvancedOpen && (
            <button
              type="button"
              onClick={() => setIsAdvancedOpen(true)}
              title={i18n.t("more_filters")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: 999,
                border: "1px solid",
                borderColor: advancedFilters.some((f) => f.key === fileTypeFilter)
                  ? "#7c3aed"
                  : "#e5e7eb",
                backgroundColor: advancedFilters.some((f) => f.key === fileTypeFilter)
                  ? "#f3e8ff"
                  : "#ffffff",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.15s ease",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                stroke={
                  advancedFilters.some((f) => f.key === fileTypeFilter) ? "#6b21a8" : "#6b7280"
                }
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}

          {/* Gelişmiş filtreler: açıksa göster, sonda − butonu */}
          {isAdvancedOpen && (
            <>
              {advancedFilters.map((opt) => {
                const active = fileTypeFilter === opt.key;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFileTypeFilter(opt.key)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: active ? "#7c3aed" : "#e5e7eb",
                      backgroundColor: active ? "#f3e8ff" : "#ffffff",
                      color: active ? "#6b21a8" : "#374151",
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Icon size={14} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
              {/* − butonu: sonda, kapatmak için */}
              <button
                type="button"
                onClick={() => setIsAdvancedOpen(false)}
                title={i18n.t("collapse")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  stroke="#6b7280"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  fill="none"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className="library-myfiles-grid"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "0 10px 10px",
        }}
      >
        {isDrafts && (
          <>
            {!drafts && (
              <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 13 }}>
                {i18n.t("loading") || "Yükleniyor..."}
              </div>
            )}
            {drafts?.error && (
              <div style={{ textAlign: "center", padding: 32, color: "#ef4444", fontSize: 13 }}>
                {i18n.t("files_load_error") || "Dosyalar yüklenemedi"}
              </div>
            )}
            {drafts && !drafts.error && visibleDrafts.length === 0 && (
              <EmptyState title={i18n.t("library_video_drafts_empty")} />
            )}
            {visibleDrafts.length > 0 && (
              <AssetGrid columns={3} cellSize={120} gap={10}>
                {visibleDrafts.map((d) => {
                  const title = d.title || dt("Untitled");
                  return (
                    <AssetCell
                      key={d.id}
                      ratio={0.75}
                      flush
                      title={title}
                      label={title}
                      onAdd={() =>
                        window.dispatchEvent(
                          new CustomEvent("aidea:openDirectorProject", {
                            detail: { projectId: d.id },
                          })
                        )
                      }
                      badge={
                        <>
                          <span style={aiChipStyle}>
                            {dt(`DraftStatus.${DRAFT_PHASE[d.status] || d.status}`, {
                              defaultValue: d.status,
                            })}
                            {d.renderReady ? ` · ${dt("Rendered")}` : ""}
                          </span>
                          {d.origin === "import" && (
                            <span style={{ ...aiChipStyle, left: "auto", right: 4 }}>
                              {dt("Imported")}
                            </span>
                          )}
                        </>
                      }
                    >
                      {d.thumbUrl ? (
                        <img
                          src={d.thumbUrl}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <AssetPlaceholder type="video" />
                      )}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          padding: "2px 4px",
                          fontSize: 10,
                          background: "rgba(255,255,255,0.85)",
                          color: "#475569",
                        }}
                      >
                        {dt("DraftShots", { count: d.shots || 0 })} ·{" "}
                        {dt("DraftSeconds", { n: Math.round(d.totalSec || 0) })} ·{" "}
                        {relativeTime(d.updatedAt)}
                      </div>
                    </AssetCell>
                  );
                })}
              </AssetGrid>
            )}
          </>
        )}
        {!isDrafts && (loading || stale) && (
          <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 13 }}>
            {i18n.t("loading") || "Yükleniyor..."}
          </div>
        )}
        {error && !loading && !stale && (
          <div style={{ textAlign: "center", padding: 32, color: "#ef4444", fontSize: 13 }}>
            {error}
          </div>
        )}
        {!loading && !stale && !error && displayFiles.length === 0 && (
          <EmptyState
            title={
              sceneFilter === "all"
                ? i18n.t("no_files_found") || "Dosya bulunamadı"
                : i18n.t("no_scene_media") || "Bu sahnede medya yok"
            }
            hint={i18n.t("try_other_search")}
          />
        )}
        {!loading && !stale && displayFiles.length > 0 && isAudioFilter && (
          <>
            {/* Gizli metadata preloader: her ses için süreyi (çalmadan) yükler.
                Görünmez ama mount olduğu için tarayıcı metadata'yı çeker. */}
            <div style={{ display: "none" }}>
              {displayFiles.map((file, index) => {
                const cardId = file.id || index;
                const url = audioRowUrl(file);
                if (!url || durations[cardId] || durationFailed[cardId]) return null;
                return (
                  <audio
                    key={`meta-${cardId}`}
                    src={url}
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      const d = e.currentTarget.duration;
                      if (isFinite(d) && d > 0)
                        setDurations((prev) => (prev[cardId] ? prev : { ...prev, [cardId]: d }));
                      else setDurationFailed((prev) => ({ ...prev, [cardId]: true }));
                    }}
                    onError={() => setDurationFailed((prev) => ({ ...prev, [cardId]: true }))}
                  />
                );
              })}
            </div>
            {audioMetaReady ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {displayFiles.map(renderAudioRow)}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 48,
                }}
              >
                <LuLoaderCircle
                  size={32}
                  style={{ animation: "spin 1s linear infinite", color: "#4f46e5" }}
                />
              </div>
            )}
          </>
        )}
        {!loading &&
          !stale &&
          displayFiles.length > 0 &&
          fileTypeFilter !== "audio" &&
          fileTypeFilter !== "clonevoice" && (
            <AssetGrid columns={3} cellSize={120} gap={10}>
              {displayFiles.map((file, index) => {
                const name = file?.name || file?.filename || `file-${index + 1}`;
                const thumbnail = file?.thumbnail || file?.url || file?.src;
                const fileType = (file?.fileType || file?.type || "file").toLowerCase();
                const cardId = file.id || index;
                // Animasyonlu webp video olarak eklenir: kutu/önizleme oynayabilen
                // mp4'ü kullanır, grid küçük resmi webp kalır.
                const url =
                  (fileType === "video" && file?.raw?.metadata?.mp4Url) ||
                  file?.raw?.fileVirtualPath ||
                  thumbnail;
                const isReplaceMode = fileModalResult && fileModalResult.id;
                const plugin = resolvePlugin(fileType, isClm);
                const handleAdd = () => {
                  if (!url) return;
                  let isMatch = true;
                  if (isReplaceMode && fileModalResult?.accept && fileType) {
                    isMatch = fileModalResult.accept.toLowerCase().includes(fileType.toLowerCase());
                  }
                  if (!isReplaceMode || !isMatch) {
                    return addFileAsBox(fileType, url, {
                      motionSource: motionSourceFromFileRow(file?.raw, url),
                    });
                  }
                  if (onElementSelected) onElementSelected(name, url, fileType, cardId);
                };
                return (
                  <AssetCell
                    key={cardId}
                    ratio={0.75}
                    flush={fileType === "image" || isImageLikeUrl(thumbnail)}
                    title={name}
                    label={name}
                    dragProps={
                      plugin
                        ? {
                            className: "palette-item floatingEditorBox",
                            name: plugin.name,
                            "data-plugin": plugin.name,
                            "data-url": url,
                            "data-media": plugin.media,
                            "data-type": "MyFiles",
                          }
                        : {}
                    }
                    onAdd={plugin ? handleAdd : undefined}
                    previewLabel={fileType === "audio" ? i18n.t("listen") : i18n.t("preview")}
                    previewIcon={
                      fileType === "audio" ? (
                        playingId === cardId ? (
                          <LuPause size={14} />
                        ) : (
                          <LuPlay size={14} />
                        )
                      ) : undefined
                    }
                    onPreview={
                      url
                        ? (e) => {
                            e.stopPropagation();
                            if (fileType === "audio") togglePlayAudio(cardId, url);
                            else setPreviewFile({ id: cardId, type: fileType, url, name });
                          }
                        : undefined
                    }
                    badge={
                      file.generated ? (
                        <span style={aiChipStyle}>{i18n.t("library_ai")}</span>
                      ) : null
                    }
                    actions={[
                      {
                        key: "details",
                        icon: <LuInfo size={14} />,
                        title: i18n.t("library_details"),
                        onClick: () =>
                          setDetailsFile({
                            file,
                            type: fileType,
                            durationText: file.raw?.fileDuration
                              ? formatDuration(file.raw.fileDuration)
                              : "",
                          }),
                      },
                      {
                        key: "download",
                        icon: <LuDownload size={14} />,
                        title: i18n.t("download") || "İndir",
                        onClick: () => downloadFile(url, name),
                      },
                      // Image → blank Director project with it as the start frame; video → imported project.
                      // Both open on the Edit step (the video edit timeline).
                      (fileType === "image" || fileType === "video") &&
                        !!url && {
                          key: "director",
                          icon: <LuFilm size={14} />,
                          title: dt("OpenInDirector"),
                          onClick: () =>
                            openInDirector(
                              fileType === "video"
                                ? { importUrl: url }
                                : { blank: true, imageUrl: url, aspect: imageAspect(file.raw) }
                            ),
                        },
                      {
                        key: "delete",
                        icon: <LuTrash2 size={14} />,
                        title: i18n.t("delete") || "Sil",
                        danger: true,
                        onClick: () => {
                          if (!file.id && file.id !== 0) return;
                          setConfirmDelete({ id: file.id, name });
                        },
                      },
                    ]}
                  >
                    {(fileType === "image" || isImageLikeUrl(thumbnail)) && thumbnail ? (
                      <LazyAssetImage
                        src={thumbnail}
                        alt={name}
                        fallback={<AssetPlaceholder type="file" />}
                      />
                    ) : fileType === "video" && thumbnail ? (
                      <VideoCellThumb url={thumbnail} />
                    ) : (
                      <AssetPlaceholder type={fileType} />
                    )}
                  </AssetCell>
                );
              })}
            </AssetGrid>
          )}
        {!loading && !stale && pagination.hasNext && (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: "8px 20px",
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                color: "#374151",
                fontSize: 13,
                fontWeight: 600,
                cursor: loadingMore ? "default" : "pointer",
                opacity: loadingMore ? 0.6 : 1,
              }}
            >
              {loadingMore
                ? i18n.t("loading") || "Yükleniyor..."
                : i18n.t("load_more") || "Daha Fazla Yükle"}
            </button>
          </div>
        )}
      </div>

      {/* Portal to <body> so the overlay escapes the Library panel's transformed
          ancestor — otherwise position:fixed anchors to the panel and the dialog
          appears over the left panel instead of centered on the viewport. */}
      {confirmDelete &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15,23,42,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000000,
            }}
            onClick={() => setConfirmDelete(null)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 16,
                padding: "28px 28px 20px",
                width: 320,
                boxShadow: "0 20px 40px rgba(15,23,42,0.2)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: 15, fontWeight: 600, color: "#111827", marginBottom: 8 }}>
                {i18n.t("delete_file_title")}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, lineHeight: 1.5 }}>
                {i18n.t("delete_file_confirm_prefix")}
                <b style={{ color: "#374151" }}>{confirmDelete.name}</b>
                {i18n.t("delete_file_confirm_suffix")}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    color: "#374151",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {i18n.t("cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const { id } = confirmDelete;
                    setConfirmDelete(null);
                    deleteIdealStudioFile(id)
                      .then(() => {
                        setFiles((prev) => prev.filter((f) => f.id !== id));
                        // Cache'lerden de düş → başka filtreye geçince silinen dosya geri gelmesin.
                        for (const k of Object.keys(filesCacheRef.current)) {
                          const c = filesCacheRef.current[k];
                          filesCacheRef.current[k] = {
                            ...c,
                            files: c.files.filter((f) => f.id !== id),
                          };
                        }
                        if (previewFile && previewFile.id === id) setPreviewFile(null);
                      })
                      .catch((err) => console.error("Delete file failed", err));
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: "#dc2626",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {i18n.t("delete")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {previewFile && (
        <AppModal
          show
          onClose={() => setPreviewFile(null)}
          title={previewFile.name}
          centered
          size="lg"
          showSceneToggle={false}
          className="libraryPreviewModal"
        >
          {(() => {
            // URL uzantısından da tip tespit et — backend mime/extension eksik
            // veya "animation" gibi standart-dışı bir değer döndürdüğünde
            // iframe fallback'ine düşüp tarayıcının indirme tetiklemesini engeller.
            const cleanUrl = (previewFile.url || "").split("?")[0];
            const dotIndex = cleanUrl.lastIndexOf(".");
            const urlExt = dotIndex !== -1 ? cleanUrl.substring(dotIndex + 1).toLowerCase() : "";

            let resolvedType = previewFile.type;
            if (isImageLikeUrl(previewFile.url)) {
              resolvedType = "image";
            } else if (
              resolvedType !== "image" &&
              resolvedType !== "audio" &&
              resolvedType !== "video"
            ) {
              if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(urlExt)) {
                resolvedType = "image";
              } else if (["mp3", "wav", "ogg"].includes(urlExt)) {
                resolvedType = "audio";
              } else if (["mp4", "mov", "webm", "mkv"].includes(urlExt)) {
                resolvedType = "video";
              }
            }

            if (resolvedType === "audio") {
              return (
                <audio
                  controls
                  autoPlay
                  style={{ width: "100%", marginTop: 8 }}
                  src={previewFile.url}
                />
              );
            }
            if (resolvedType === "video") {
              return (
                <video
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    maxHeight: "70vh",
                    backgroundColor: "#000000",
                    marginTop: 4,
                  }}
                  src={previewFile.url}
                />
              );
            }
            if (resolvedType === "image") {
              return (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: 12,
                    marginTop: 4,
                    backgroundColor: "#000000",
                  }}
                />
              );
            }
            return (
              <iframe
                title={previewFile.name}
                src={previewFile.url}
                style={{
                  width: "100%",
                  height: "60vh",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  marginTop: 4,
                  backgroundColor: "#ffffff",
                }}
              />
            );
          })()}
        </AppModal>
      )}
    </div>
  );
};

MyFilesPanel.propTypes = {
  isClm: PropTypes.bool,
  filesUploaded: PropTypes.any,
  onNavItemsAdded: PropTypes.func.isRequired,
  onIndexSelected: PropTypes.func.isRequired,
  onNavItemSelected: PropTypes.func.isRequired,
  navItemsIds: PropTypes.array.isRequired,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any,
  boxes: PropTypes.object.isRequired,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default MyFilesPanel;
